import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {LocalContentProcessModel} from '../../../../models/LocalContentProcessModel';
import {ColumnKey, IEnhancedColumn} from '../../../common/EnhancedDetailsList/IEnhancedDetailsList';
import {
  OpenColumn,
  OtherColumn,
  RichtextColumn,
  SelectColumn
} from '../../../common/EnhancedDetailsList';
import {Description} from '../../Process/components/Description';
import {LinksListColumn} from '../../../common/EnhancedDetailsList/ColumnTypes/LinksListColumn';
import {IColumn} from '@fluentui/react';
import {useMasterProcess} from '../../../../hooks/services/useMasterProcess';

export const useMasterProcessLocalContent = (isOpen: boolean, masterProcessIds?: number[]) => {
  const {getLocalContentProcess} = useMasterProcess();
  const [processes, setProcesses] = useState<LocalContentProcessModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const columns = useMemo(() => {
    const cols: IEnhancedColumn<LocalContentProcessModel>[] = [
      {
        key: ColumnKey.Url,
        name: '',
        data: OpenColumn,
        onRender: (item, index, column) => OpenColumn.renderer(item.url, column as IColumn)
      },
      {
        key: ColumnKey.Title,
        name: 'Title',
        flexGrow: 1,
        minWidth: 300,
        exportToExcel: true,
        onExportToExcel: (item, column) => {
          return {
            text: item.title,
            link: window.location.origin + item.url
          };
        }
      },
      {
        key: ColumnKey.UnitTitle,
        name: 'Unit',
        data: SelectColumn,
        minWidth: 200,
        exportToExcel: true,
      },
      {
        key: 'description',
        name: 'Description',
        minWidth: 300,
        data: OtherColumn,
        onRender: item => {
          return item.description ? (
            <div style={{whiteSpace: 'break-spaces', maxWidth: '100%'}}>
              <Description unitTitle={item.unitTitle} isOpen={false} description={item.description} />
            </div>
          ) : (
            <></>
          );
        },
        exportToExcel: true,
        onExportToExcel: (item, column) =>
          RichtextColumn.getColumnValueForExcel(item.description?.content)
      },
      {
        key: 'documents',
        name: 'Local Documents',
        minWidth: 250,
        data: LinksListColumn,
        exportToExcel: true
      },
      {
        key: 'externalLinks',
        name: 'Local Links',
        minWidth: 250,
        data: LinksListColumn,
        exportToExcel: true
      },
      {
        key: 'activityInputs',
        name: 'Local Activity inputs',
        minWidth: 250,
        data: LinksListColumn,
        exportToExcel: true
      },
      {
        key: 'activityOutputs',
        name: 'Local Activity outputs',
        minWidth: 250,
        data: LinksListColumn,
        exportToExcel: true
      }
    ];
    return cols;
  }, []);

  const getData = useCallback(async (ids: number[]) => {
    setIsLoading(true);
    try {
      const response = await getLocalContentProcess(ids)
      if (response.result && !response.apiCode) {
        setProcesses(response.result);
      } else {
        setProcesses([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [getLocalContentProcess]);

  useEffect(() => {
    if (!isOpen || !masterProcessIds || masterProcessIds.length === 0) {
      setProcesses([]);
      return;
    }
    getData(masterProcessIds);
  }, [isOpen, getData, masterProcessIds]);

  return useMemo(
    () => ({
      isLoading,
      columns,
      processes
    }),
    [isLoading, columns, processes]
  );
};
